import React, { useMemo, useState } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from "chart.js";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import {
  buildBarDataset,
  buildChartOptions,
  ChartContainer,
} from "../../../../shared/UIParts/chartUtils";
import { InsuranceCoverageInfo } from "../../../../interfaces/DTO/mislaka_xml_insurence_models";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CoverageAmountsStackedChartProps {
  coveragesMap: Record<string, InsuranceCoverageInfo[]>;
}

type PeriodType = "yearly" | "monthly";

export const CoverageAmountsStackedChart: React.FC<CoverageAmountsStackedChartProps> = ({ coveragesMap }) => {
  const [periodType, setPeriodType] = useState<PeriodType>("yearly");
  const [hasTruncated, setHasTruncated] = useState(false);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriodType(event.target.value as PeriodType);
  };

  const chartData = useMemo(() => {
    const dataByCoverage: Record<string, Record<number, number>> = {};
    let maxYear = 0;
    let minYear = 9999;
    let hasFarFuture = false;
    const currentYear = new Date().getFullYear();

    const calculateMonths = (start: string, end: string): number => {
      if (!start || !end || start.length < 6 || end.length < 6) return 12;
      const sy = parseInt(start.slice(0, 4));
      const sm = parseInt(start.slice(4, 6));
      const ey = parseInt(end.slice(0, 4));
      const em = parseInt(end.slice(4, 6));
      return (ey - sy) * 12 + (em - sm) + 1;
    };

    let legalEndYears: number[] = [];
    Object.values(coveragesMap).flat().forEach(entry => {
      if (!entry.end_period || entry.end_period.length < 6) return;
      const endYear = parseInt(entry.end_period.slice(0, 4));
      if (endYear - currentYear <= 50) {
        legalEndYears.push(endYear);
      }
    });
    const globalMaxAllowedYear = Math.max(...legalEndYears);

    Object.entries(coveragesMap).forEach(([coverageName, entries]) => {
      const filteredEntries = entries.filter(e => e.insurance_amount && e.insurance_amount > 0);
      dataByCoverage[coverageName] = {};
      const seenYears = new Set<number>();

      filteredEntries.forEach((entry, idx) => {
        if (!entry.start_period || !entry.end_period) return;
        let startYear = parseInt(entry.start_period.slice(0, 4));
        let endYear = parseInt(entry.end_period.slice(0, 4));

        if (idx === filteredEntries.length - 1 && endYear > globalMaxAllowedYear) {
          endYear = globalMaxAllowedYear;
          hasFarFuture = true;
        }

        const months = calculateMonths(entry.start_period, entry.end_period);
        const normalizedAmount = periodType === "yearly"
          ? entry.insurance_amount!
          : entry.insurance_amount! / months;

        for (let year = startYear; year <= endYear; year++) {
          if (year >= currentYear && !seenYears.has(year)) {
            dataByCoverage[coverageName][year] = normalizedAmount;
            seenYears.add(year);
            maxYear = Math.max(maxYear, year);
            minYear = Math.min(minYear, year);
          }
        }
      });
    });

    setHasTruncated(hasFarFuture);

    const years = Array.from({ length: maxYear - currentYear + 1 }, (_, i) => currentYear + i);

    const datasets = Object.entries(dataByCoverage).map(([coverageName, yearData], index) => {
      const color = `hsl(${(index * 60) % 360}, 60%, 60%)`;
      const data = years.map((y) => Math.round(yearData[y] || 0));
      return buildBarDataset({
        label: coverageName,
        data,
        backgroundColor: color,
        borderColor: color,
      });
    });

    return {
      labels: years.map(String),
      datasets,
    };
  }, [coveragesMap, periodType]);

  const chartOptions = useMemo(() => buildChartOptions({
    isLineChart: false,
    isStacked: true,
    tooltip: {
      callbacks: {
        footer: (tooltipItems: TooltipItem<"bar">[]) => {
          const sum = tooltipItems.reduce((acc, item) => acc + (item.raw as number), 0);
          return `סך הכל: ${sum.toLocaleString()}`;
        },
      },
    },
  }), []);

  // בדיקה אם יש datasets להצגה
  const hasData = chartData.datasets.length > 0 &&
                  chartData.datasets.some(dataset =>
                    dataset.data &&
                    dataset.data.length > 0 &&
                    dataset.data.some((value: any) => value > 0)
                  );

  if (!hasData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        השתנות הכיסוי הביטוחי לאורך זמן
      </Typography>
      <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
        <InputLabel id="period-type-label">סוג תקופה</InputLabel>
        <Select
          labelId="period-type-label"
          value={periodType}
          onChange={handlePeriodChange}
          label="סוג תקופה"
        >
          <MenuItem value="yearly">שנתי</MenuItem>
          <MenuItem value="monthly">חודשי</MenuItem>
        </Select>
      </FormControl>
      <ChartContainer height={350}>
        <Chart type="bar" data={chartData} options={chartOptions} />
      </ChartContainer>
      {hasTruncated && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          * תקופת הכיסוי המוצגת מוגבלת לשנת סיום כללית עקב חוסר ודאות בתאריך סיום.
        </Typography>
      )}
    </Box>
  );
};
